import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Field, LoadingState, Panel, TextInput } from '@bcecloud/portal-ui';
import { readLeadStats, type StatsInterval } from '@/lib/leads';
import { money } from '@/lib/report-window';
import {
  leadsPerDayForTarget,
  valuePerLead,
  yearlyRevenueAtPace,
} from '@/lib/revenue-math';

/**
 * The planning target the calculator opens on. Every lever is editable, so
 * this is a starting point, not a setting.
 */
const DEFAULT_TARGET = 10_000_000;

/** YYYY-MM-DD for `days` days ago (UTC is fine at month granularity). */
function daysAgo(days: number): string {
  return new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
}

/**
 * Where today's numbers come from:
 * - PACE (leads/day) — the last 90 days, current enough to steer by.
 * - CONVERSION and AVERAGE CONTRACT — leads created 15 to 3 months ago, a
 *   MATURED cohort: a lead needs time to close, so a recent window would
 *   understate both and quietly inflate the required pace.
 */
const PACE_DAYS = 90;
const COHORT = { fromDaysAgo: 455, toDaysAgo: 90 } as const;
const DAY_INTERVAL: StatsInterval = 'day';

interface ObservedInputs {
  readonly leadsPerDay: number;
  readonly conversionPct: number;
  readonly averageContract: number;
}

/**
 * "Path to the number": today's leads/day, conversion and average contract
 * next to a what-if version of each, with the yearly revenue both produce
 * and the leads/day a target demands. Change any lever and the end number
 * moves — the planning surface for "how do we get to $10M".
 *
 * @returns The calculator panel.
 */
export function RevenueMathPanel(): React.ReactElement {
  const observed = useQuery({
    queryKey: ['revenue-math-observed'],
    queryFn: async ({ signal }) => {
      const [pace, cohort] = await Promise.all([
        readLeadStats({ from: daysAgo(PACE_DAYS), to: daysAgo(-1), interval: DAY_INTERVAL }, signal),
        readLeadStats(
          { from: daysAgo(COHORT.fromDaysAgo), to: daysAgo(COHORT.toDaysAgo), interval: DAY_INTERVAL },
          signal,
        ),
      ]);
      const signed = cohort.totals.signed;
      return {
        leadsPerDay: pace.totals.perDay,
        conversionPct:
          cohort.totals.leads === 0 ? 0 : Math.round((signed / cohort.totals.leads) * 1000) / 10,
        averageContract: signed === 0 ? 0 : Math.round(cohort.totals.signedValue / signed),
      } satisfies ObservedInputs;
    },
    staleTime: 5 * 60_000,
  });

  if (observed.isPending) {
    return (
      <Panel className="p-5">
        <LoadingState label="Loading revenue math…" />
      </Panel>
    );
  }
  if (observed.isError) {
    // The page's own error surfaces the API problem; the panel just steps aside.
    return <></>;
  }
  return <Calculator observed={observed.data} />;
}

function Calculator({ observed }: { readonly observed: ObservedInputs }): React.ReactElement {
  const [target, setTarget] = useState(DEFAULT_TARGET);
  const [leadsPerDay, setLeadsPerDay] = useState<number | null>(null);
  const [conversionPct, setConversionPct] = useState<number | null>(null);
  const [averageContract, setAverageContract] = useState<number | null>(null);

  const whatIf = {
    leadsPerDay: leadsPerDay ?? observed.leadsPerDay,
    conversionPct: conversionPct ?? observed.conversionPct,
    averageContract: averageContract ?? observed.averageContract,
  };
  const assumptions = useMemo(
    () => ({ conversionRate: whatIf.conversionPct / 100, averageContract: whatIf.averageContract }),
    [whatIf.conversionPct, whatIf.averageContract],
  );
  const projected = yearlyRevenueAtPace(whatIf.leadsPerDay, assumptions);
  const todayRevenue = yearlyRevenueAtPace(observed.leadsPerDay, {
    conversionRate: observed.conversionPct / 100,
    averageContract: observed.averageContract,
  });
  const neededPace = leadsPerDayForTarget(target, assumptions);
  const paceGap = neededPace === null ? null : neededPace - whatIf.leadsPerDay;
  const edited = leadsPerDay !== null || conversionPct !== null || averageContract !== null;

  return (
    <Panel className="mt-4 p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-portal-card-title font-bold text-portal-ink">Path to the number</h2>
          <p className="text-portal-micro text-portal-muted">
            Yearly revenue = leads/day × 365 × conversion × average contract. Today's pace is the
            last {PACE_DAYS} days; conversion and contract value come from leads old enough to have
            closed (created 15–3 months ago). Move any lever to see the end number move.
          </p>
        </div>
        {edited ? (
          <Button
            variant="secondary"
            onClick={() => {
              setLeadsPerDay(null);
              setConversionPct(null);
              setAverageContract(null);
            }}
          >
            Reset to today
          </Button>
        ) : null}
      </div>

      <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <LeverField
          id="rm-leads"
          label="Leads per day"
          hint={`Today: ${observed.leadsPerDay.toLocaleString()}`}
          value={whatIf.leadsPerDay}
          step={0.5}
          onChange={setLeadsPerDay}
        />
        <LeverField
          id="rm-conversion"
          label="Conversion to signed (%)"
          hint={`Today: ${observed.conversionPct.toLocaleString()}%`}
          value={whatIf.conversionPct}
          step={0.5}
          onChange={setConversionPct}
        />
        <LeverField
          id="rm-contract"
          label="Average contract ($)"
          hint={`Today: ${money.format(observed.averageContract)}`}
          value={whatIf.averageContract}
          step={100}
          onChange={setAverageContract}
        />
        <LeverField
          id="rm-target"
          label="Yearly revenue target ($)"
          hint="The number to hit"
          value={target}
          step={250_000}
          onChange={(next) => setTarget(next ?? DEFAULT_TARGET)}
        />
      </div>

      <div className="mt-5 grid gap-4 sm:grid-cols-3">
        <div>
          <p className="text-portal-micro font-bold tracking-[0.6px] text-portal-muted uppercase">
            At these levers
          </p>
          <p className="mt-1 text-[24px] leading-none font-bold text-portal-ink tabular-nums">
            {money.format(Math.round(projected))}<span className="text-portal-small font-normal text-portal-muted"> / year</span>
          </p>
          <p className="mt-1 text-portal-micro text-portal-muted">
            {money.format(Math.round(valuePerLead(assumptions)))} per lead
            {edited ? ` · today's levers produce ${money.format(Math.round(todayRevenue))}` : ''}
          </p>
        </div>
        <div>
          <p className="text-portal-micro font-bold tracking-[0.6px] text-portal-muted uppercase">
            To reach {money.format(target)}
          </p>
          <p className="mt-1 text-[24px] leading-none font-bold text-portal-ink tabular-nums">
            {neededPace === null ? '—' : `${(Math.round(neededPace * 10) / 10).toLocaleString()} leads/day`}
          </p>
          <p className="mt-1 text-portal-micro text-portal-muted">
            at {whatIf.conversionPct.toLocaleString()}% and {money.format(whatIf.averageContract)} per contract
          </p>
        </div>
        <div>
          <p className="text-portal-micro font-bold tracking-[0.6px] text-portal-muted uppercase">
            Gap
          </p>
          <p
            className={`mt-1 text-[24px] leading-none font-bold tabular-nums ${
              paceGap !== null && paceGap <= 0 ? 'text-portal-success' : 'text-portal-ink'
            }`}
          >
            {paceGap === null
              ? '—'
              : paceGap <= 0
                ? 'On pace'
                : `+${(Math.round(paceGap * 10) / 10).toLocaleString()} leads/day`}
          </p>
          <p className="mt-1 text-portal-micro text-portal-muted">
            {paceGap === null
              ? 'These levers never reach the target.'
              : paceGap <= 0
                ? 'The levers above already cover the target.'
                : `${Math.round(paceGap * 365).toLocaleString()} more leads over a year`}
          </p>
        </div>
      </div>
    </Panel>
  );
}

/** One editable lever: a labeled number input that reports null when cleared. */
function LeverField({
  id,
  label,
  hint,
  value,
  step,
  onChange,
}: {
  readonly id: string;
  readonly label: string;
  readonly hint: string;
  readonly value: number;
  readonly step: number;
  readonly onChange: (value: number | null) => void;
}): React.ReactElement {
  return (
    <Field label={label} htmlFor={id} hint={hint}>
      <TextInput
        id={id}
        type="number"
        min={0}
        step={step}
        value={Number.isFinite(value) ? value : 0}
        onChange={(event) => {
          const next = Number(event.target.value);
          onChange(event.target.value === '' || !Number.isFinite(next) ? null : next);
        }}
      />
    </Field>
  );
}
